/**
 * Owner console surface — LIMS-parity (../lims's `/owner/*` route tree,
 * `OwnerLoginPage` + `OwnerLayout`): its own entry URL, its own sign-in
 * (email + 6-digit TOTP, no password — `POST /auth/platform/owner-login`),
 * and its own violet-accented dark chrome, deliberately distinct from the
 * teal hospital shell so nobody mistakes one surface for the other.
 *
 * Unlike LIMS this rides the SAME session store as the rest of the app (one
 * platform JWT, `useAuth`) — hospital already had a platform token audience
 * and guards, so a second token store would buy nothing. The pages inside
 * (`OwnerOverviewPage`, `TenantsPage`, …) are unchanged; only the shell and
 * the way in moved.
 */
import { useState } from 'react';
import { useScrollToProblem } from '@/lib/scroll-to-problem';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Hexagon, LogOut, ShieldCheck } from 'lucide-react';
import type { PublicLandingDto } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { publicGet } from '@/lib/public-api';
import { cn } from '@/lib/utils';
import { useForcedDark } from '@/lib/theme';
import { Alert, BrandLoader, Button, Field, Input } from '@/components/ui/primitives';
import { ownerNavFor, ownerNavGroups, PLATFORM_ROLE_LABELS, type OwnerNavItem } from './owner-nav';
import { useBranchRequestCount } from './BranchRequestsPage';

function NavCount({ item, counts }: { item: OwnerNavItem; counts: Partial<Record<OwnerNavItem['key'], number>> }) {
  const count = counts[item.key] ?? 0;
  if (count <= 0) return null;
  return (
    <span className="ms-auto rounded-full bg-warning/20 px-1.5 text-[10.5px] font-semibold text-warning" aria-label={`${count} waiting`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

const BACKDROP = 'bg-background bg-[radial-gradient(1200px_700px_at_80%_-10%,hsl(var(--primary)/0.16),transparent)] bg-fixed text-foreground';

const BRAND_MARK = 'grid shrink-0 place-items-center border border-primary/40 bg-primary/10 shadow-[0_0_14px_-2px_hsl(var(--primary)/0.5)]';

export function OwnerPortal() {
  const { user, loading, needsTwoFactorSetup, mustChangePassword } = useAuth();
  useForcedDark();

  if (loading) {
    return (
      <div className={cn('grid min-h-screen place-items-center', BACKDROP)}>
        <BrandLoader />
      </div>
    );
  }
  if (!user || !user.isPlatformUser) return <OwnerLoginPage />;
  // A contact person holds a platform token but not this console.
  if (user.platformRole === 'contact_person') return <Navigate to="/client" replace />;
  if (needsTwoFactorSetup) return <Navigate to="/two-factor" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return <OwnerShell />;
}

/* ────────────────────────── sign-in (email + TOTP) ─────────────────────── */

type Stage = 'email' | 'enrolmentCode' | 'enrollment' | 'code';

function OwnerLoginPage() {
  const { signInOwner } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [enrolmentCode, setEnrolmentCode] = useState('');
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signInOwner(
        email.trim(),
        stage === 'email' || stage === 'enrolmentCode' ? undefined : code,
        stage === 'enrolmentCode' ? enrolmentCode.trim() : undefined,
      );
      if ('enrollment' in result) {
        setEnrollment(result.enrollment);
        setStage('enrollment');
      } else {
        navigate('/owner', { replace: true });
      }
    } catch (caught) {
      const err = caught as { code?: string; message?: string };
      if (err.code === 'two_factor_required') {
        setStage('code');
      } else {
        setError(err.message ?? 'Sign-in failed');
        if (stage !== 'email') setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('flex min-h-screen items-center justify-center p-4', BACKDROP)}>
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span className={cn('mx-auto h-12 w-12 rounded-2xl', BRAND_MARK)}>
            <Hexagon className="h-6 w-6 text-primary" aria-hidden />
          </span>
          <h1 className="mt-3 text-xl font-semibold text-foreground">HIS Owner console</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {stage === 'email' && 'Sign in with your owner email — no password, your authenticator is the key.'}
            {stage === 'enrolmentCode' && 'Enter the one-time enrolment code another owner gave you.'}
            {stage === 'enrollment' && 'First sign-in: add this secret to your authenticator app.'}
            {stage === 'code' && 'Enter the 6-digit code from your authenticator app. Signing in for the first time? Use “First sign-in? I have an enrolment code” below.'}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-xl border bg-card/80 p-6 text-card-foreground shadow-sm backdrop-blur">
          {stage === 'email' && (
            <Field label="Email" htmlFor="owner-email" required>
              <Input
                id="owner-email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
          )}

          {stage === 'enrolmentCode' && (
            <Field label="Enrolment code" htmlFor="owner-enrolment-code" required>
              <Input
                id="owner-enrolment-code"
                autoFocus
                autoComplete="off"
                className="font-mono tracking-widest"
                value={enrolmentCode}
                onChange={(e) => setEnrolmentCode(e.target.value.toUpperCase())}
                required
              />
            </Field>
          )}

          {stage === 'enrollment' && enrollment && (
            <div className="rounded-md border bg-muted/60 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Secret key — shown once, save it now
              </p>
              <p className="mt-1 select-all break-all font-mono text-sm text-primary">{enrollment.secret}</p>
              <p className="mt-2 break-all text-[11px] text-muted-foreground/80">{enrollment.otpauthUrl}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Add it to Google Authenticator, Authy or 1Password (account: {email}), then confirm with the code it
                shows.
              </p>
            </div>
          )}

          {(stage === 'enrollment' || stage === 'code') && (
            <Field label="Authenticator code" htmlFor="owner-totp" required>
              <Input
                id="owner-totp"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                className="tracking-[0.4em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </Field>
          )}

          {error && <Alert variant="destructive">{error}</Alert>}

          <Button
            type="submit"
            className="w-full"
            loading={busy}
            disabled={
              stage === 'enrolmentCode'
                ? enrolmentCode.replace(/[^A-Z2-7]/gi, '').length < 10
                : stage !== 'email' && code.length !== 6
            }
          >
            {stage === 'email' || stage === 'enrolmentCode'
              ? 'Continue'
              : stage === 'enrollment'
                ? 'Confirm and sign in'
                : 'Sign in'}
          </Button>

          {(stage === 'email' || stage === 'code') && (
            <button
              type="button"
              className="block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => {
                setStage('enrolmentCode');
                setError(null);
              }}
              disabled={!email.trim()}
            >
              First sign-in? I have an enrolment code
            </button>
          )}

          {stage !== 'email' && (
            <button
              type="button"
              className="block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => {
                setStage('email');
                setCode('');
                setEnrolmentCode('');
                setEnrollment(null);
                setError(null);
              }}
            >
              Use a different email
            </button>
          )}
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Hospital staff?{' '}
          <a className="text-primary underline underline-offset-2" href="/login">
            Sign in here
          </a>
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────── the shell ─────────────────────────────── */

/** The cloud's own version, for the footer (plan §3.7) — from the public landing summary, so it costs no owner call. */
function CloudVersionFooter() {
  const landing = useQuery({
    queryKey: ['public', 'landing'],
    queryFn: () => publicGet<PublicLandingDto>('/landing'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const latest = landing.data?.latestStable;
  return (
    <footer className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center justify-between gap-2 px-6 py-4 text-[11px] text-muted-foreground no-print">
      <span>
        Cloud version <span className="font-mono text-foreground/80">{landing.data?.cloudVersion ?? '—'}</span>
        {latest && (
          <>
            {' '}
            · latest published stable <span className="font-mono text-foreground/80">{latest.version}</span>
          </>
        )}
      </span>
      <NavLink to="/owner/releases" className="text-primary underline-offset-2 hover:underline">
        Releases
      </NavLink>
    </footer>
  );
}

function OwnerShell() {
  const mainRef = useScrollToProblem();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = ownerNavFor(user?.platformRole);
  const landing = nav[0];
  const branchRequests = useBranchRequestCount(nav.some((item) => item.key === 'branch_requests'));
  const counts = { branch_requests: branchRequests.data?.pending ?? 0 };

  if (location.pathname.replace(/\/+$/, '') === '/owner' && landing && landing.key !== 'overview') {
    return <Navigate to={landing.to} replace />;
  }

  return (
    <div className={cn('flex h-dvh flex-col overflow-hidden print:block print:h-auto print:overflow-visible', BACKDROP)}>
      <header className="relative z-30 flex h-14 shrink-0 items-center gap-3 border-b border-primary/20 bg-card/80 px-6 backdrop-blur-md no-print">
        <div className="flex shrink-0 items-center gap-2.5">
          <span className={cn('h-8 w-8 rounded-[10px]', BRAND_MARK)}>
            <Hexagon className="h-4 w-4 text-primary" aria-hidden />
          </span>
          <div className="whitespace-nowrap leading-tight">
            <p className="text-[13px] font-semibold tracking-wide text-foreground">HIS OWNER CONSOLE</p>
            <p className="text-[9.5px] uppercase tracking-[0.18em] text-primary/80">Master administration</p>
          </div>
        </div>

        <span className="ms-3 hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10.5px] text-primary xl:flex">
          <ShieldCheck className="h-3 w-3" aria-hidden /> {PLATFORM_ROLE_LABELS[user?.platformRole ?? ''] ?? 'Staff'}
        </span>

        <div className="flex-1" />

        <div className="ms-auto flex shrink-0 items-center gap-3">
          <span className="hidden max-w-[200px] truncate text-[11.5px] text-muted-foreground 2xl:block">{user?.email}</span>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 whitespace-nowrap text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/login')}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Hospital login
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 whitespace-nowrap border-primary/30 text-primary hover:bg-primary/10"
            onClick={() => {
              void signOut().then(() => navigate('/owner', { replace: true }));
            }}
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </header>

      {/* Small screens get the nav as a scrollable strip under the header. */}
      <nav className="flex shrink-0 items-center gap-1 overflow-x-auto border-b px-4 py-2 lg:hidden no-print">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground',
                isActive && 'bg-primary/10 text-primary',
              )
            }
          >
            <item.icon className="h-3.5 w-3.5" aria-hidden />
            {item.label}
            <NavCount item={item} counts={counts} />
          </NavLink>
        ))}
      </nav>

      <div className="flex min-h-0 w-full flex-1 print:block">
        <aside className="hidden w-56 shrink-0 border-e lg:block no-print">
          <nav className="h-full space-y-5 overflow-y-auto px-3 py-6" aria-label="Owner console">
            {ownerNavGroups(nav).map((section) => (
              <div key={section.group}>
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">{section.group}</p>
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                          isActive && 'bg-primary/10 text-primary',
                        )
                      }
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {item.label}
                      <NavCount item={item} counts={counts} />
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
        <main ref={mainRef} className="app-scroll relative min-w-0 flex-1 overflow-y-auto overscroll-contain print:overflow-visible">
          <div className="mx-auto w-full max-w-[1376px] px-6 py-8">
            <Outlet />
          </div>
          <CloudVersionFooter />
        </main>
      </div>
    </div>
  );
}
